import type { Geometry, Polygon, MultiPolygon } from 'geojson';

/**
 * Adjacency-aware four-color graph coloring for Presentation-mode zone
 * fills. Pure function — no React, no hooks — so callers can memoize the
 * result with `useMemo` keyed off a stable id-set hash.
 *
 * Why four-color: the four-color theorem proves every planar map can be
 * colored with at most 4 colors such that no two adjacent regions share a
 * color. Welsh-Powell greedy coloring (sort by descending degree, assign
 * lowest unused color) achieves the optimum on real-world planar adjacency
 * graphs ~99% of the time.
 *
 * Adjacency is detected with **bounding-box overlap only** — fast and
 * predictable (O(N²) with cheap arithmetic, no turf calls). This produces
 * some false positives (zones whose bboxes overlap but whose polygons
 * don't actually touch) but those are harmless: a false positive forces
 * "near but non-touching" zones onto different colors, which is a stronger
 * separation than required, not a weaker one. Real-world Vehix zones built
 * from unioned ZIP polygons have hundreds of vertices each; running
 * `turf.booleanIntersects` per pair was freezing the browser, so the
 * algorithm now uses bbox-only adjacency. Cost: <5ms even for 200 zones.
 *
 * Returns a `Map<zone_id, paletteIndex>` where paletteIndex is 0–3 (or
 * higher if the graph required more — rare). Callers should use
 * `paletteIndex % VEHIX_ZONE_PALETTE.length` defensively.
 */
export interface ZoneForColoring {
  id: string;
  geometry: Geometry;
}

interface ZoneWithBbox {
  id: string;
  bbox: [number, number, number, number]; // [minX, minY, maxX, maxY]
}

const BBOX_BUFFER = 1e-6; // ~0.1m at the equator — catches "shared edge" cases.

function bboxesOverlap(a: ZoneWithBbox['bbox'], b: ZoneWithBbox['bbox']): boolean {
  // Reject if one bbox is entirely to the side of the other.
  if (a[2] + BBOX_BUFFER < b[0]) return false;
  if (b[2] + BBOX_BUFFER < a[0]) return false;
  if (a[3] + BBOX_BUFFER < b[1]) return false;
  if (b[3] + BBOX_BUFFER < a[1]) return false;
  return true;
}

/**
 * Compute bbox manually from coordinates without going through turf.
 * Avoids the per-zone Feature-wrap + turf.bbox cost (small but adds up
 * across hundreds of zones) and removes a dependency cycle risk in tight
 * memo loops.
 */
function computeBbox(geometry: Polygon | MultiPolygon): [number, number, number, number] {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  const visit = (coord: number[]) => {
    const x = coord[0]!;
    const y = coord[1]!;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  };

  if (geometry.type === 'Polygon') {
    geometry.coordinates.forEach(ring => ring.forEach(visit));
  } else {
    geometry.coordinates.forEach(polygon =>
      polygon.forEach(ring => ring.forEach(visit))
    );
  }

  return [minX, minY, maxX, maxY];
}

function isSupportedPolygon(geometry: Geometry): geometry is Polygon | MultiPolygon {
  return geometry.type === 'Polygon' || geometry.type === 'MultiPolygon';
}

export function computeZoneColorAssignment(
  zones: ReadonlyArray<ZoneForColoring>
): Map<string, number> {
  const assignment = new Map<string, number>();
  if (zones.length === 0) return assignment;

  // Step 1: Pre-compute bboxes once. Skip zones whose geometry isn't a
  // polygon shape (defensive against malformed data).
  const withBbox: ZoneWithBbox[] = [];
  zones.forEach(zone => {
    if (!isSupportedPolygon(zone.geometry)) return;
    try {
      withBbox.push({ id: zone.id, bbox: computeBbox(zone.geometry) });
    } catch {
      // Defensive: malformed geometry shouldn't crash the whole coloring.
    }
  });

  // Step 2: Build adjacency graph via bbox overlap. Bbox-only check is
  // cheap (8 number comparisons per pair) and produces a slight
  // over-estimate of true adjacency — that over-estimate is harmless
  // because forcing non-touching zones onto different colors only adds
  // separation, never removes it.
  const adjacency = new Map<string, Set<string>>();
  withBbox.forEach(z => adjacency.set(z.id, new Set()));

  for (let i = 0; i < withBbox.length; i++) {
    for (let j = i + 1; j < withBbox.length; j++) {
      const a = withBbox[i]!;
      const b = withBbox[j]!;
      if (!bboxesOverlap(a.bbox, b.bbox)) continue;
      adjacency.get(a.id)!.add(b.id);
      adjacency.get(b.id)!.add(a.id);
    }
  }

  // Step 3: Welsh-Powell greedy coloring. Sort vertices by descending
  // degree so high-adjacency zones get colored first (when more palette
  // choices are still available).
  const sorted = withBbox
    .map(z => ({ id: z.id, degree: adjacency.get(z.id)!.size }))
    .sort((a, b) => b.degree - a.degree);

  sorted.forEach(({ id }) => {
    const neighborColors = new Set<number>();
    adjacency.get(id)!.forEach(neighborId => {
      const c = assignment.get(neighborId);
      if (c !== undefined) neighborColors.add(c);
    });
    let color = 0;
    while (neighborColors.has(color)) color += 1;
    assignment.set(id, color);
  });

  return assignment;
}
